import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Row, Col, ListGroup, Button, Card, InputGroup, Form, Badge } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Countdown from './Countdown';
import { toast } from 'react-toastify';
import { calculateBidPaid, listBids } from '../../actions/bidActions';
import { bidPaidReset } from '../../reducers/bidReducers';
import { Link, useNavigate } from 'react-router-dom';
import PaymentModal from './PaymentModal';
import BidList from './BidList';
import { FaWifi, FaArrowLeft } from 'react-icons/fa';
import './BidSection.css';

function BidSection({ dispatch, productID }) {
    const navigate = useNavigate();
    const {
        productReducers: { product },
        bidReducers: { bidPaidError, bidPaidLoading, bidPaidSuccess, bid, bids },
        userReducers: { user }
    } = useSelector((state) => state);

    const [highestBid, setHighestBid] = useState('');
    const [bidCount, setBidCount] = useState('');
    const [closeTime, setCloseTime] = useState('');
    const [startTime, setStartTime] = useState('');
    const [bidInstance, setBidInstance] = useState('');
    const [isStarted, setIsStarted] = useState(false);
    const [countdownFinished, setCountdownFinished] = useState(false);
    const [offer, setOffer] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [wsConnected, setWsConnected] = useState(false);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const socketRef = useRef(null);
    const maxReconnectAttempts = 5;
    const reconnectInterval = 3000; // 3 seconds
    const [isExtended, setIsExtended] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [auctionActive, setAuctionActive] = useState(false);
    const [hasShownError, setHasShownError] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [isFlashing, setIsFlashing] = useState(false);

    useEffect(() => {
        if (product) {
            const currentDate = new Date();
            const startDate = new Date(product.startDate);
            const endDate = new Date(product.endDate);
            const hasStarted = currentDate > startDate;
            const hasEnded = currentDate > endDate;
            const timeRemaining = Math.max(0, (endDate - currentDate) / 1000);

            setIsStarted(hasStarted);
            setCountdownFinished(hasEnded);
            setHighestBid(product.currentHighestBid);
            setBidCount(product.totalBids);
            setCloseTime(product.endDate);
            setStartTime(product.startDate);
            setTimeLeft(timeRemaining);
            
            // Only set auction as active if it has started and not finished
            setAuctionActive(hasStarted && !hasEnded);
            
            console.log('Auction status:', { 
                hasStarted, 
                hasEnded, 
                isActive: hasStarted && !hasEnded,
                currentTime: currentDate.toISOString(),
                startTime: startDate.toISOString(),
                endTime: endDate.toISOString()
            });
        }
    }, [product]);

    // Update timeLeft every second
    useEffect(() => {
        if (!product || countdownFinished) return;

        const timer = setInterval(() => {
            const currentDate = new Date();
            const endDate = new Date(product.endDate);
            const timeRemaining = Math.max(0, (endDate - currentDate) / 1000);
            setTimeLeft(timeRemaining);
        }, 1000);

        return () => clearInterval(timer);
    }, [product, countdownFinished]);

    const handleCountdownUpdate = (countdown) => {
        const isFinished = countdown.days === 0 && countdown.hours === 0 && countdown.minutes === 0 && countdown.seconds === 0;
        
        if (isFinished && !countdownFinished) {
            setCountdownFinished(true);
            setAuctionActive(false);
            
            // Close WebSocket connection if auction just ended
            if (socketRef.current) {
                console.log('Auction ended, closing WebSocket connection');
                socketRef.current.close();
                setWsConnected(false);
            }

            // Show auction ended toast
            toast.info("Auction has ended!", {
                position: "bottom-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "dark",
            });

            // Refresh bid list one final time
            if (user) {
                dispatch(listBids(productID));
            }
        }
    };

    const modalShow = () => setShowModal(true);
    const modalClose = () => setShowModal(false);

    const submitHandler = async (e) => {
        e.preventDefault();

        let newBid = {
            'product': productID,
            'bid': offer,
        };

        await dispatch(calculateBidPaid(newBid));
        setBidInstance(newBid);
    };

    // Function to connect to WebSocket
    const connectWebSocket = useCallback(() => {
        // Don't attempt to connect if the auction is not active
        if (!auctionActive) {
            console.log('Auction is not active, not connecting to WebSocket');
            return;
        }

        // Close any existing socket
        if (socketRef.current && socketRef.current.readyState !== WebSocket.CLOSED) {
            socketRef.current.close();
        }

        // Create new WebSocket connection
        console.log('Connecting to WebSocket for active auction');
        socketRef.current = new WebSocket(`${process.env.REACT_APP_BASE_WEBSOCKET}/ws/auctions/${productID}/`);
        
        // Basic handlers
        socketRef.current.onmessage = (e) => {
            const wsData = JSON.parse(e.data);
            
            if (wsData.type === 'extension') {
                const extensionData = JSON.parse(wsData.message);
                setCloseTime(extensionData.new_end_date);
                setIsExtended(true);
                setIsFlashing(true);
                
                // Show extension toast
                toast.info("Auction time extended by 5 minutes!", {
                    position: "bottom-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "dark",
                });
                
                // Reset extension states after 5 seconds
                setTimeout(() => {
                    setIsExtended(false);
                    setIsFlashing(false);
                }, 5000);
            } else if (wsData.type === 'bid_update') {
                const wsDataMessage = JSON.parse(wsData.message);
                if (wsDataMessage) {
                    setHighestBid(wsDataMessage.amount);
                    setBidCount(wsDataMessage.bid_count);
                    if (wsDataMessage.closes_in) {
                        setCloseTime(wsDataMessage.closes_in);
                    }
                    if (user) {
                        dispatch(listBids(productID));
                    }
                }
            }
        };

        socketRef.current.onclose = (e) => {
            console.log('WebSocket connection closed', e.code, e.reason);
            setWsConnected(false);
            
            // Don't attempt to reconnect if the auction is not active
            if (!auctionActive) {
                console.log('Auction is not active, not attempting to reconnect');
                return;
            }
            
            // Only show error toast if we haven't shown one yet and we're not in the process of reconnecting
            if (!hasShownError && !isReconnecting) {
                toast.error("Live bid updates disconnected. Please refresh the page.", {
                    position: "bottom-right",
                    autoClose: false,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "dark",
                });
                setHasShownError(true);
            }
            
            // Attempt to reconnect if not intentionally closed
            if (reconnectAttempts < maxReconnectAttempts) {
                console.log(`WebSocket reconnecting... Attempt ${reconnectAttempts + 1} of ${maxReconnectAttempts}`);
                setIsReconnecting(true);
                setTimeout(() => {
                    setReconnectAttempts(prevAttempts => prevAttempts + 1);
                    connectWebSocket();
                }, reconnectInterval);
            }
        };

        socketRef.current.onerror = (error) => {
            console.error('WebSocket error:', error);
            setWsConnected(false);
        };

        socketRef.current.onopen = () => {
            console.log('WebSocket connection established');
            setWsConnected(true);
            setReconnectAttempts(0);
            setIsReconnecting(false);
            setHasShownError(false);
            
            // Only show the toast on the very first successful connection, not on reconnects
            if (!isReconnecting) {
                toast.info("Live bid updates connected", {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "dark",
                });
            }
        };
    }, [auctionActive, productID, reconnectAttempts, user, dispatch, hasShownError, isReconnecting]);

    // Set up WebSocket connection
    useEffect(() => {
        // Only connect to WebSocket if productID exists and auction is active
        if (!productID || !auctionActive) {
            // If we have an existing connection and the auction is not active, close it
            if (socketRef.current && socketRef.current.readyState !== WebSocket.CLOSED) {
                console.log('Auction is not active, closing existing WebSocket connection');
                socketRef.current.close();
                setWsConnected(false);
            }
            return;
        }

        connectWebSocket();

        // Clean up function to close WebSocket connection
        return () => {
            if (socketRef.current) {
                console.log('Component unmounting, closing WebSocket connection');
                socketRef.current.close();
            }
        };
    }, [productID, auctionActive, connectWebSocket]);

    // Monitor auction status changes to manage WebSocket connection
    useEffect(() => {
        // If auction status changes from active to inactive, close the connection
        if (!auctionActive && socketRef.current) {
            console.log('Auction became inactive, closing WebSocket connection');
            socketRef.current.close();
            setWsConnected(false);
        }
    }, [auctionActive]);

    useEffect(() => {
        if (bidPaidLoading) {
            toast.info("Response is pending...", {
                position: "top-right",
                autoClose: false,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: false,
                draggable: false,
                theme: "dark",
            });
        }
        
        if (!bidPaidLoading) {
            if (bidPaidSuccess) {
                toast.dismiss();
                let success = async () => {
                    await dispatch(bidPaidReset());
                    modalShow();
                    
                    toast.success(`Your bid payment calculated for this product.`, {
                        position: "top-right",
                        autoClose: 1000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        theme: "dark",
                    });
                };
                success();
            }

            if (bidPaidError) {
                toast.dismiss();
                toast.error(bidPaidError, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "dark",
                });
            }
        }
    }, [bidPaidLoading, bidPaidSuccess, dispatch, productID, bidPaidError, bid]);

    // Initial bid list loading
    useEffect(() => {
        if (productID && user) {
            dispatch(listBids(productID));
        }
    }, [productID, user, dispatch]);

    return (
        product && (
            <>
                {closeTime && (
                    <Col md={6} xl={3} className='mt-md-5 mt-lg-0'>
                        <div className="d-flex justify-content-end mb-2">
                            <Button 
                                variant="link" 
                                className="text-dark p-0" 
                                onClick={() => navigate(-1)}
                                title="Go back"
                            >
                                <FaArrowLeft size={18} />
                            </Button>
                        </div>
                        <Card className='shadow border-0'>
                            <ListGroup>
                                <ListGroup.Item>
                                    <Row className='d-flex'>
                                        <Col md={7}>
                                            {!countdownFinished 
                                                ? isStarted 
                                                    ? 'Current highest bid: ' 
                                                    : 'Starting Price: ' 
                                                : 'Sold for: '}
                                        </Col>
                                        <Col md={5} className='h4 justify-content-end d-flex'>
                                            <strong>R{isStarted ? highestBid : product.price}</strong>
                                        </Col>
                                    </Row>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <Row className={countdownFinished ? 'justify-content-center' : ''}>
                                        {!countdownFinished && (
                                            <Col md={4}>
                                                {isStarted ? 'Closes in:' : 'Starts in:'}
                                            </Col>
                                        )}
                                        <Col md={!countdownFinished ? 8 : ''} className={!countdownFinished ? 'justify-content-end d-flex' : 'h5 text-center mt-2'}>
                                            <div className={`countdown ${timeLeft < 300 ? 'warning' : ''} ${isExtended ? 'extended' : ''} ${isFlashing ? 'flashing' : ''}`}>
                                                <Countdown endDate={new Date(isStarted ? closeTime : startTime)} onCountdownUpdate={handleCountdownUpdate} />
                                            </div>
                                        </Col>
                                    </Row>
                                    {isExtended && (
                                        <Row className="mt-2">
                                            <Col>
                                                <div className="extension-notice">
                                                    <span className="text-success">
                                                        <i className="fas fa-clock me-1"></i>
                                                        Auction extended by 5 minutes!
                                                    </span>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                    {countdownFinished && (
                                        <Row className="mt-2">
                                            <Col>
                                                <div className="text-center text-muted">
                                                    Auction has ended
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <Row>
                                        <Col md={8}>
                                            Bids:
                                        </Col>
                                        <Col md={4} className='justify-content-end d-flex'>
                                            <strong>{bidCount}</strong>&nbsp;
                                            {wsConnected && isStarted && !countdownFinished ? (
                                                <Badge bg="success" pill className="ms-1 d-flex align-items-center">
                                                    <FaWifi size={10} className="me-1" />
                                                    <span className="small">LIVE</span>
                                                </Badge>
                                            ) : null}
                                        </Col>
                                    </Row>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <Row className='justify-content-center'>
                                        {user ? (
                                            product.user === user._id && isStarted ? (
                                                <Col md={12} className='d-flex justify-content-center'>
                                                    <Button disabled className="w-100 btn btn-danger my-2">
                                                        You can not bid your product.
                                                    </Button>
                                                </Col>
                                            ) : (
                                                !countdownFinished ? (
                                                    isStarted ? (
                                                        <Col md={12}>
                                                            <Form onSubmit={submitHandler}>
                                                                <InputGroup className='mb-1'>
                                                                    <InputGroup.Text style={{cursor:'default'}}>R</InputGroup.Text>
                                                                    <Form.Control
                                                                        required
                                                                        type="number"
                                                                        placeholder='Enter your bid'
                                                                        value={offer}
                                                                        min={1}
                                                                        maxLength={8}
                                                                        onChange={(e) => setOffer(e.target.value)}
                                                                        onKeyDown={(e) => {
                                                                            if(e.key === ',' || e.key === '.'){
                                                                                e.preventDefault()
                                                                            }
                                                                        }}
                                                                        onPaste={(e) => {
                                                                            const paste = e.clipboardData.getData('text')
                                                                            if (!(paste === '' || /^[0-9\b]+$/.test(paste))){
                                                                                e.preventDefault()
                                                                            }
                                                                        }}
                                                                    />
                                                                </InputGroup>
                                                                <Col md={12} className='d-flex justify-content-center'>
                                                                    <Button type='submit' className="w-100 rounded my-2 btn-dark">Place a bid</Button>
                                                                </Col>
                                                            </Form>
                                                        </Col>
                                                    ) : (
                                                        <Col md={12} className='d-flex justify-content-center'>
                                                            <div className="text-center text-muted py-2 w-100">
                                                                Auction has not yet started.
                                                            </div>
                                                        </Col>
                                                    )
                                                ) : (
                                                    <Col md={12} className='d-flex justify-content-center'>
                                                        <div className="text-center w-100">
                                                            {bidCount > 0 ? (
                                                                <div className="text-muted">
                                                                    <div className="mb-2">Auction has ended</div>
                                                                    <div className="small">Final bid: R{highestBid}</div>
                                                                </div>
                                                            ) : (
                                                                <div className="text-muted">
                                                                    <div className="mb-2">Auction has ended</div>
                                                                    <div className="small">No bids were placed</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Col>
                                                )
                                            )
                                        ) : (
                                            <Col md={12} className='d-flex justify-content-center'>
                                                <Link to="/login" className="w-100 btn btn-danger my-2">
                                                    You must be logged in.
                                                </Link>
                                            </Col>
                                        )}
                                    </Row>
                                </ListGroup.Item>
                                <ListGroup.Item>
                                    <div>
                                        <strong>Bid History</strong>
                                        <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '10px' }}>
                                            {bids && bids.length > 0 ? (
                                                <ListGroup variant="flush">
                                                    {[...bids].reverse().map((bid, index) => (
                                                        <BidList 
                                                            key={index} 
                                                            bid={bid} 
                                                            isCurrentUserBid={user && bid.user === user._id} 
                                                        />
                                                    ))}
                                                </ListGroup>
                                            ) : (
                                                <div className="text-center text-muted py-3">
                                                    No bids yet
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            </ListGroup>
                        </Card>
                        <PaymentModal bidInstance={bidInstance} show={showModal} onHide={modalClose} />
                    </Col>
                )}
            </>
        )
    );
}

export default BidSection;
